import {Alert, Dialog} from 'platform/components';
import {Box, Grid, Show, Text, VStack} from 'platform/foundation';

import type {ReactNode} from 'react';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import type {AuctionOffer} from '../../model/auctionView.types';
import {useAuctionFormat} from '../components/useAuctionFormat';

interface OfferDetailDialogProps {
  offer: AuctionOffer | null;
  roundNumber: number | null;
  isVatNotDeductible: boolean;
  onClose: () => void;
  // undefined: the seller cannot change the selection.
  onSelect?: (offerId: string) => void;
}

function Field(props: {label: string; children: ReactNode}) {
  return (
    <VStack>
      <Text size="xSmall" color="secondary">
        {props.label}
      </Text>
      {props.children}
    </VStack>
  );
}

// Prototype v11 offer detail dialog, opened from the offer row actions.
export function OfferDetailDialog(props: OfferDetailDialogProps) {
  const format = useAuctionFormat();
  const offer = props.offer;
  const select = props.onSelect;

  return (
    <Dialog
      isOpen={offer !== null}
      size="default"
      title={offer?.traderName}
      onClose={props.onClose}
      buttons={[
        {
          title: i18n.t('entity.auction.offers.messages'),
          variant: 'secondary',
          isDisabled: true,
        },
        {
          title: i18n.t('entity.auction.offers.buyerRatings'),
          variant: 'secondary',
          isDisabled: true,
        },
        {
          title: i18n.t('entity.auction.offers.selectThisOffer'),
          variant: 'primary',
          isDisabled: select === undefined || offer === null || offer.rejectedAt !== null,
          onClick: () => {
            if (offer) {
              select?.(offer.offerId);
            }
            props.onClose();
          },
          'data-testid': 'auction-offer-detail-select',
        },
      ]}
      data-testid="auction-offer-detail"
    >
      <Show when={offer}>
        {(current) => (
          <VStack spacing={4}>
            <Show when={current.rejectedAt}>
              {(rejectedAt) => (
                <VStack spacing={4}>
                  <Alert
                    variant="error"
                    title={i18n.t('entity.auction.offers.offerRejected')}
                    message={i18n.t('entity.auction.offers.rejectedSelectAnother', {
                      defaultValue: 'This offer was withdrawn or rejected. Select another offer.',
                    })}
                    data-testid="auction-offer-detail-rejected"
                  />
                  <Grid columns={2} spacing={4}>
                    <Field label={i18n.t('entity.auction.decline.source')}>
                      <Text>
                        {current.rejectionSource
                          ? i18n.t(`entity.auction.decline.sources.${current.rejectionSource}`)
                          : EMPTY_PLACEHOLDER}
                      </Text>
                      <Text size="small" color="secondary">
                        {format.dateTime(rejectedAt)}
                      </Text>
                    </Field>
                    <Field label={i18n.t('entity.auction.decline.evidence')}>
                      <Text overflowWrap="anywhere">
                        {current.rejectionEvidence ?? EMPTY_PLACEHOLDER}
                      </Text>
                    </Field>
                    <Field label={i18n.t('entity.auction.decline.customerNote')}>
                      <Text overflowWrap="anywhere">
                        {current.rejectionNote ?? EMPTY_PLACEHOLDER}
                      </Text>
                    </Field>
                  </Grid>
                </VStack>
              )}
            </Show>
            <Grid columns={[1, 2]} spacing={4}>
              <Field label={i18n.t('entity.auction.offers.price')}>
                <Text alternative strikethrough={current.rejectedAt !== null}>
                  {format.money(current.amount)}
                </Text>
                <Show when={props.isVatNotDeductible}>
                  <Text size="small" color="secondary">
                    {i18n.t('entity.auction.confirmation.vatNotDeductible')}
                  </Text>
                </Show>
              </Field>
              <Field label={i18n.t('general.labels.received')}>
                <Text>{format.dateTime(current.correctedAt ?? current.placedAt)}</Text>
              </Field>
              <Field label={i18n.t('entity.auction.offers.buyerNote')}>
                <Text overflowWrap="anywhere">{current.note ?? EMPTY_PLACEHOLDER}</Text>
              </Field>
              <Field label={i18n.t('entity.auction.offers.rating')}>
                <Text>
                  {current.traderRating === null
                    ? i18n.t('entity.auction.offers.notRated')
                    : i18n.t('entity.auction.offers.ratingValue', {
                        rating: format.rating(current.traderRating),
                      })}
                </Text>
                <Text size="small" color="secondary">
                  {[
                    current.traderRatingsCount !== null &&
                      format.count(
                        'entity.auction.offers.ratingsCount',
                        current.traderRatingsCount
                      ),
                    current.traderTransactionsCount !== null &&
                      format.count(
                        'entity.auction.offers.transactionsCount',
                        current.traderTransactionsCount
                      ),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </Field>
            </Grid>
            <Grid columns={2} spacing={4}>
              <Field label={i18n.t('general.labels.company')}>
                <Text overflowWrap="anywhere">{current.traderCompany ?? EMPTY_PLACEHOLDER}</Text>
              </Field>
              <Field label={i18n.t('entity.auction.offers.tier', {defaultValue: 'Tier'})}>
                <Text>{current.traderTier}</Text>
              </Field>
            </Grid>
            <Field label={i18n.t('entity.auction.offers.contact')}>
              <Text>{current.traderContactName ?? EMPTY_PLACEHOLDER}</Text>
              <Text size="small" overflowWrap="anywhere">
                {[current.traderPhone, current.traderEmail].filter(Boolean).join(' · ')}
              </Text>
            </Field>
            <VStack spacing={2}>
              <Text alternative>{i18n.t('entity.auction.offers.bidHistory')}</Text>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.auction.header.round', {number: props.roundNumber ?? 1})}
              </Text>
              {/* shortcut: the API keeps only the current amount, so earlier bids are not listed. Add rows when the BE exposes bid history. */}
              <Box paddingVertical={2}>
                <Grid columns={2}>
                  <VStack>
                    <Text>{i18n.t('entity.auction.offers.currentBid')}</Text>
                    <Text size="xSmall" color="secondary">
                      {format.dateTime(current.correctedAt ?? current.placedAt)}
                    </Text>
                  </VStack>
                  <Box>
                    <Text align="right" color="link" alternative>
                      {format.money(current.amount)}
                    </Text>
                  </Box>
                </Grid>
              </Box>
            </VStack>
          </VStack>
        )}
      </Show>
    </Dialog>
  );
}
